import { computed, ref, watch, onScopeDispose } from 'vue';
import { storeToRefs } from 'pinia';
import type { useWorkspaceStore } from '../stores/workspace';
import type { ExportEntry, ExportOptions } from '../types/export';
import { chapterExportOrder, expandedExportIds } from '../lib/exportSelection';
import { downloadWorkspaceExport } from '../lib/exportDownload';
import { defaultExportPageLayout } from '../lib/exportPageLayout';

export function useWorkspaceExport(store: ReturnType<typeof useWorkspaceStore>, flushSave: () => Promise<void>) {
  const { workspace, entities, activeEntity } = storeToRefs(store);
  const exportOpen = ref(false);
  const exporting = ref(false);
  const exportError = ref('');
  const exportTitle = ref('Chronis Export');
  const exportEntries = ref<ExportEntry[]>([]);
  const exportOptions = ref<ExportOptions>({ includeSummary: true, includeFields: true, includeProfiles: true });
  const exportLayout = ref(defaultExportPageLayout());
  const resolvedCount = computed(() => expandedExportIds(exportEntries.value, entities.value).length);
  let controller: AbortController | null = null;
  watch(
    () => workspace.value?.id,
    () => {
      controller?.abort();
      controller = null;
      exportOpen.value = false;
      exporting.value = false;
      exportError.value = '';
      exportEntries.value = [];
    }
  );
  onScopeDispose(() => controller?.abort());

  function openExport() {
    exportError.value = '';
    exportEntries.value =
      activeEntity.value?.status === 'active' ? [{ kind: 'entity', entityId: activeEntity.value.id }] : [];
    exportTitle.value = 'Chronis Export';
    exportOpen.value = true;
  }
  function selectChapters() {
    exportEntries.value = entities.value
      .filter((entity) => entity.status === 'active' && entity.templateCategory === 'kapitel')
      .sort(chapterExportOrder)
      .map((entity) => ({ kind: 'entity', entityId: entity.id }));
  }
  async function exportSelection() {
    if (!workspace.value || exporting.value || !exportEntries.value.length) return;
    const workspaceId = workspace.value.id;
    const current = new AbortController();
    controller = current;
    exporting.value = true;
    exportError.value = '';
    try {
      await flushSave();
      if (current.signal.aborted || workspace.value?.id !== workspaceId) return;
      await downloadWorkspaceExport(
        workspaceId,
        {
          format: 'pdf',
          title: exportTitle.value.trim() || 'Chronis Export',
          entries: exportEntries.value,
          options: exportOptions.value,
          layout: exportLayout.value
        },
        current.signal
      );
      if (!current.signal.aborted) exportOpen.value = false;
    } catch (error) {
      if (!current.signal.aborted)
        exportError.value = error instanceof Error ? error.message : 'Der Export konnte nicht erstellt werden.';
    } finally {
      if (controller === current) {
        exporting.value = false;
        controller = null;
      }
    }
  }
  return {
    exportOpen,
    exporting,
    exportError,
    exportTitle,
    exportEntries,
    exportOptions,
    exportLayout,
    resolvedCount,
    openExport,
    selectChapters,
    exportSelection
  };
}
